import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { modalProps } from "Components/FilePrint/utils/modalProps";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { locateFileInDocuments } from "utils/locateFileInDocuments";
import { locateFileInViews } from "utils/locateFileInViews";
import { ItemType } from "antd/lib/menu/interface";
import { waitFor } from "utils/waitFor";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";

interface SharedAttachmentsTableItemActionsProps extends WithTranslation, WithModal {
  file: API_FileAttachment;
  editMode?: boolean;
  onRemove?: (fileId: number) => void;
  disabled?: boolean;
  hideNavigateTo?: boolean;
  hideModal?: () => void;
}

export const SharedAttachmentsTableItemActions = withTranslation(I18N_NS.PROJECT)(
  withModal(
    class extends React.Component<SharedAttachmentsTableItemActionsProps> {
      handleMenuClick = ({ key, domEvent }) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        this.doFileClick(key, this.props.file as API_FileAttachment);
      };

      get menuItems(): ItemType[] {
        const { t, file, onRemove, disabled } = this.props;
        const items: ItemType[] = [];
        if (onRemove) {
          items.push({
            key: "remove",
            disabled: disabled,
            label: (
              <>
                <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} className="mr-5" />
                {t("Remove")}
              </>
            ),
          });
        }
        if (file.fileActions) {
          if (file.fileActions.download) {
            items.push({
              key: "download",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "download"]} fixedWidth={true} className="mr-5" />
                  {t("Download")}
                </>
              ),
            });
          }
          if (file.viewers) {
            if (file.viewers.bcfViewer) {
              items.push({
                key: "bcf_view",
                label: (
                  <>
                    {" "}
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("Open with BCF Viewer")}
                  </>
                ),
              });
            }
            if (file.viewers.webViewer) {
              items.push({
                key: "apryse_view",
                label: (
                  <>
                    {" "}
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with WebViewer")}
                  </>
                ),
              });
            } else if (file.viewers.easyViewer) {
              items.push({
                key: "easy_view",
                label: (
                  <>
                    {" "}
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Easy Viewer")}
                  </>
                ),
              });
            } else if (file.viewers.office365) {
              items.push({
                key: "wopi_view",
                label: (
                  <>
                    {" "}
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Office Online")}
                  </>
                ),
              });
            } else if (file.viewers.onlineViewer) {
              items.push({
                key: "online_view",
                label: (
                  <>
                    {" "}
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Online Viewer")}
                  </>
                ),
              });
            }
            if (file.viewers.bimViewerSupported) {
              items.push({
                key: "xkt_view",
                disabled: !file.viewers.bimViewer,
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "cube"]} fixedWidth={true} className="mr-5" />
                    {file.viewers.bimViewer ? t("View with BIM Viewer") : t("BIM Viewer (being processed)")}
                  </>
                ),
              });
            }
          }
        }
        if (!this.props.hideNavigateTo) {
          items.push({
            key: "jump_to",
            label: (
              <>
                {" "}
                <FontAwesomeIcon icon={["far", "folder"]} fixedWidth={true} className="mr-5" />
                {t("Navigate to ...")}
              </>
            ),
          });
        }
        return items;
      }

      doFileClick = (key: string, data: API_FileAttachment) => {
        const { t, showModal } = this.props;
        switch (key) {
          case "download":
            documentsTableStore.downloadFiles([data]);
            break;
          case "bcf_view":
            showModal(BCFViewerModal, {
              modalProps: {
                ...modalProps,
                maskClosable: true,
                width: "100%",
                title: `${t("File")}: ${data.name}`,
                style: { maxWidth: "1300px" },
                className: "full-height-modal with-drawer bcf-modal",
                zIndex: 1013,
              },
              componentProps: { fileId: +data.id },
            });
            break;
          case "apryse_view":
            apryseViewerStore.fetch(+data.id, {
              fileName: data.name,
              canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
            });
            break;
          case "easy_view":
            pdfViewerStore.fetch(+data.id, {
              fileName: data.name,
              canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
            });
            break;
          case "wopi_view":
            wopiViewerStore.fetch(+data.id, { fileName: data.name, edit: data.viewers.office365Edit });
            break;
          case "online_view":
            onlineViewerStore.fetch(+data.id, { fileName: data.name });
            break;
          case "xkt_view":
            (async () => {
              if (this.props.hideModal) {
                this.props.hideModal();
              }
              if (xktViewerStore.isVisible) {
                xktViewerStore.hide();
              }
              // wait for modal animation to finish
              await waitFor(500);
              xktViewerStore.initViewer({ fileName: data.name, fileId: data.id, parentFolderId: data.parentId });
            })();
            break;
          case "jump_to":
            data.accessibleByViewId
              ? locateFileInViews(data.accessibleByViewId, data.id)
              : locateFileInDocuments(data.parentId, data.id);
            break;
          case "remove":
            this.props.onRemove(data.id);
            break;
          default:
            break;
        }
      };
      render() {
        return (
          <Dropdown
            menu={{ items: this.menuItems, onClick: this.handleMenuClick, className: "documents-actions" }}
            trigger={["click"]}
            styles={{ root: { zIndex: 999999 } }}
          >
            <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
            </div>
          </Dropdown>
        );
      }
    }
  )
);
